<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive, defineEmits, ref, computed, defineProps } from 'vue';
import { MandateDocumentCategoryEnum } from "@/enums/mandate_enum.ts";

import CardComponent from '@/components/ui/CardComponent.vue';
import validators from "@/utils/validators.ts";

import type { ValidationRule } from "vuetify/framework";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  isDraft?: boolean,
  isPending?: boolean,
  isRefused?: boolean,
}>(), {
  isDraft: true,
  isPending: false,
  isRefused: false,
});
const emits = defineEmits(['closeModal']);

const categories = computed(() => [
  { title: t('components.documentComponent.categories.dic'), value: MandateDocumentCategoryEnum.DIC },
]);
if (props.isDraft) {
  categories.value.push({ title: t('components.documentComponent.categories.other'), value: MandateDocumentCategoryEnum.OTHER });
}

// --- Document form ---
const formValid = ref<boolean>(false);
const documentFileNameRules = ref<ValidationRule[]>([...validators.documentFileNameRules])
const documentFileRules = ref<ValidationRule[]>([...validators.documentFileRules])
const documentCategoryRules = ref<ValidationRule[]>([...validators.documentCategoryRules])

const form = reactive({
  name: null as string | null,
  category: null as string | null,
  file: null as File | null,
  isUserNotified: null as boolean | null,
});

const showCheckbox = computed(() => {
  return !props.isDraft &&
    !props.isPending &&
    !props.isRefused &&
    form.category === MandateDocumentCategoryEnum.DIC
})
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        {{ t('components.documentComponent.titleCreate')}}
      </template>
      <template #item>
        <v-form v-model="formValid" class="my-4">
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="form.file"
                :label="t('components.documentComponent.inputs.file.label')"
                :rules="documentFileRules"
                accept=".pdf"
                append-inner-icon="PhPaperclip"
                clearable
                hide-details="auto"
                prepend-icon=""
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                :label="t('components.documentComponent.inputs.name.label')"
                :rules="documentFileNameRules"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.category"
                :items=categories
                :label="t('components.documentComponent.inputs.category.label')"
                :rules="documentCategoryRules"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row v-if="showCheckbox">
            <v-col cols="12">
              <v-checkbox
                v-model="form.isUserNotified"
                :label="t('components.documentComponent.inputs.isUserNotified.label')"
              >
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template #actions>
        <div :class="showCheckbox ? 'mt-0' : 'mt-2'" class="d-flex flex-row justify-space-between ga-4">
          <v-btn
            variant="outlined"
            @click.prevent="emits('closeModal', false)"
          >
            {{ t('common.buttons.cancel') }}
          </v-btn>

          <v-btn
            :disabled="!formValid"
            color="primary"
            type="submit"
            variant="flat"
            @click="emits('closeModal', form)"
          >
            {{ t('common.buttons.validate') }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>
